import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import {
  SupportTicketStatus,
  type AssigneeSummary,
  type CustomerSentiment,
  type SupportTicketPriority,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import Asterisk02Icon from "@redotech/redo-icons/asterisk-02_filled.svg";
import ClockSnoozeIcon from "@redotech/redo-icons/clock-snooze_filled.svg";
import StatusIcon from "@redotech/redo-icons/status.svg";
import UserIcon from "@redotech/redo-icons/user-01.svg";
import {
  ConversationAssigneeSummary as SupportTicketAssigneeSummary,
  ConversationResolutionSentiment as SupportTicketResolutionSentiment,
} from "@redotech/redo-model/conversation-insights";
import {
  RedoBadge,
  type RedoBadgeSize,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  ConversationAssigneeSummaryColorMap as SupportTicketAssigneeSummaryColorMap,
  ConversationAssigneeSummaryLabels as SupportTicketAssigneeSummaryLabels,
  ConversationResolutionSentimentColorMap as SupportTicketResolutionSentimentColorMap,
  ConversationResolutionSentimentLabels as SupportTicketResolutionSentimentLabels,
} from "@redotech/redo-web/conversation-insights/conversation-insights-ui";
import FolderCheckIcon from "@redotech/redo-web/icon-old/folder-check.svg";
import { memo } from "react";
import { SupportTicketStatusReadable } from "../../ui/support-ticket-status-ui";
import {
  MessageChannelIcons,
  MessageChannelReadable,
} from "../../ui/thread-channel-ui";

const statusConfig: Record<
  SupportTicketStatus,
  {
    color: "success" | "gray" | "warning" | "blue" | "error";
    Icon: typeof Asterisk02Icon;
  }
> = {
  OPEN: { color: "success", Icon: Asterisk02Icon },
  CLOSED: { color: "gray", Icon: FolderCheckIcon },
  SNOOZED: { color: "warning", Icon: ClockSnoozeIcon },
  IN_PROGRESS: { color: "blue", Icon: StatusIcon },
  HIDDEN: { color: "gray", Icon: FolderCheckIcon },
  ARCHIVED: { color: "gray", Icon: FolderCheckIcon },
  DELETED: { color: "gray", Icon: FolderCheckIcon },
  DRAFT: { color: "error", Icon: FolderCheckIcon },
};

export const StatusBadge = memo(function StatusBadge({
  status,
  size = "sm",
}: {
  status: SupportTicketStatus;
  size?: RedoBadgeSize;
}) {
  const { color, Icon } = statusConfig[status];
  return (
    <RedoBadge
      color={color}
      segmentLeading={{ type: "icon", Icon }}
      size={size}
      text={SupportTicketStatusReadable[status]}
    />
  );
});

export const SupportTicketPriorityReadable: Record<
  SupportTicketPriority,
  string
> = { URGENT: "Urgent" };

const priorityColorMap: Record<SupportTicketPriority, "error"> = {
  URGENT: "error",
};

export const PriorityBadge = memo(function PriorityBadge({
  priority,
  size = "sm",
}: {
  priority: SupportTicketPriority;
  size?: RedoBadgeSize;
}) {
  return (
    <RedoBadge
      color={priorityColorMap[priority]}
      size={size}
      text={SupportTicketPriorityReadable[priority]}
    />
  );
});

export const ChannelBadge = memo(function ChannelBadge({
  channel,
  size = "xs",
}: {
  channel: MessageChannel;
  size?: RedoBadgeSize;
}) {
  const displayName = MessageChannelReadable[channel];
  const Icon = MessageChannelIcons[channel];

  return (
    <RedoBadge
      color="gray"
      segmentLeading={{ type: "icon", Icon }}
      size={size}
      text={displayName}
    />
  );
});

export const SentimentBadge = memo(function SentimentBadge({
  sentiment,
  size = "xs",
}: {
  sentiment: CustomerSentiment;
  size?: RedoBadgeSize;
}) {
  const sentimentEnum =
    sentiment as unknown as SupportTicketResolutionSentiment;
  return (
    <RedoBadge
      color={SupportTicketResolutionSentimentColorMap[sentimentEnum]}
      size={size}
      text={SupportTicketResolutionSentimentLabels[sentimentEnum]}
    />
  );
});

export const AssigneeSummaryBadge = memo(function AssigneeSummaryBadge({
  assigneeSummary,
  size = "xs",
}: {
  assigneeSummary: AssigneeSummary;
  size?: RedoBadgeSize;
}) {
  const assigneeSummaryEnum =
    assigneeSummary as unknown as SupportTicketAssigneeSummary;
  return (
    <RedoBadge
      color={SupportTicketAssigneeSummaryColorMap[assigneeSummaryEnum]}
      size={size}
      text={SupportTicketAssigneeSummaryLabels[assigneeSummaryEnum]}
    />
  );
});

export const AssigneeBadge = memo(function AssigneeBadge({
  name,
  size = "xs",
}: {
  name: string;
  size?: RedoBadgeSize;
}) {
  return (
    <RedoBadge
      color="gray"
      segmentLeading={{ type: "icon", Icon: UserIcon }}
      size={size}
      text={name}
    />
  );
});
